import { useState } from 'react'
import { Building2, Shield, Users, CheckCircle2, AlertCircle, Globe } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || ''

type Language = 'fi' | 'en'

const translations = {
  fi: {
    title: 'Organisaation käyttöönotto',
    subtitle: 'Tilaajakanava - Taksi Helsinki',
    
    whatIsThis: 'Mistä on kyse?',
    whatIsThisDesc: 'Tilaajakanava on Taksi Helsingin laskujentarkastus- ja raportointiportaali organisaatioasiakkaille. Kirjautuminen tapahtuu turvallisesti organisaationne Azure Entra ID:n (Microsoft 365) kautta.',
    
    whatHappens: 'Mitä tapahtuu?',
    whatHappensDesc: 'Kun annat suostumuksen, organisaationne käyttäjät voivat kirjautua Tilaajakanavaan Microsoft-tunnuksillaan. Sovellus pyytää seuraavat oikeudet:',
    permissions: [
      'Käyttäjän perustiedot (nimi, sähköposti)',
      'Organisaation tunniste (tenant ID)',
    ],
    
    processTitle: 'Käyttöönoton vaiheet',
    processSteps: [
      'Syötä organisaatiosi Tenant ID alla olevaan kenttään ja paina "Tarkista" - varmistamme, että organisaationne on sallittujen joukossa.',
      'Jos organisaatio löytyy, paina "Anna suostumus" -painiketta siirtyäksesi Microsoftin kirjautumissivulle.',
      `Tarkista consent-sivulla pyydetyt valtuudet ja varmista, että Application ID on: ${CLIENT_ID}`,
      'Hyväksynnän jälkeen sinut ohjataan takaisin ja saat vahvistuksen, kun consent on kunnossa.',
    ],
    tenantIdHelp: 'Löydät Tenant ID:n Azure-portaalista: Azure Active Directory → Overview → Tenant ID',
    
    checkOrganization: 'Tarkista',
    grantConsent: 'Anna suostumus',
    
    checking: 'Tarkistetaan...',
    orgFound: 'Organisaatio löytyi',
    orgNotFound: 'Organisaatiota ei löytynyt sallittujen joukosta. Ota yhteyttä Taksi Helsinkiin.',
    alreadyVerified: 'Suostumus on jo annettu',
    
    adminTip: 'Vinkki ylläpitäjälle',
    adminTipDesc: 'Suostumuksen antamisen jälkeen voit Azure-portaalissa hallita ketkä käyttäjät pääsevät sovellukseen:',
    adminOptions: [
      'Enterprise Applications → Tilaajakanava → Properties → "Assignment required" = Yes',
      'Määritä käyttäjät tai ryhmät "Users and groups" -välilehdeltä',
    ],
    provisioningNote: 'Huom: Käyttäjiä ei provisioida automaattisesti. Organisaationne käyttäjät tulee kutsua Tilaajakanavaan etukäteen ja antaa heille tarvittavat oikeudet. Kutsumisen tekee henkilö, jolla on organisaation admin -rooli Tilaajakanavassa.',
    mfaNote: 'Suosittelemme MFA:n (monivaiheisen tunnistautumisen) käyttöönottoa organisaationne käytäntöjen mukaisesti.',
    
    errorTitle: 'Virhe',
  },
  en: {
    title: 'Organization Onboarding',
    subtitle: 'Tilaajakanava - Taksi Helsinki',
    
    whatIsThis: 'What is this?',
    whatIsThisDesc: 'Tilaajakanava is Taksi Helsinki\'s invoice reviewing and reporting portal for corporate customers. Authentication is securely handled through your organization\'s Azure Entra ID (Microsoft 365).',
    
    whatHappens: 'What happens?',
    whatHappensDesc: 'When you grant consent, your organization\'s users can sign in to Tilaajakanava with their Microsoft credentials. The application requests the following permissions:',
    permissions: [
      'User basic info (name, email)',
      'Organization identifier (tenant ID)',
    ],
    
    processTitle: 'Onboarding process',
    processSteps: [
      'Enter your organization\'s Tenant ID in the field below and click "Check" - we will verify that your organization is in the allowed list.',
      'If the organization is found, click "Grant consent" to proceed to Microsoft\'s login page.',
      `On the consent page, verify the requested permissions and confirm that the Application ID is: ${CLIENT_ID}`,
      'After approval, you will be redirected back and receive confirmation when consent is complete.',
    ],
    tenantIdHelp: 'Find your Tenant ID in Azure portal: Azure Active Directory → Overview → Tenant ID',
    
    checkOrganization: 'Check',
    grantConsent: 'Grant consent',
    
    checking: 'Checking...',
    orgFound: 'Organization found',
    orgNotFound: 'Organization not found in allowed list. Please contact Taksi Helsinki.',
    alreadyVerified: 'Consent already granted',
    
    adminTip: 'Tip for administrators',
    adminTipDesc: 'After granting consent, you can manage which users can access the application in the Azure portal:',
    adminOptions: [
      'Enterprise Applications → Tilaajakanava → Properties → "Assignment required" = Yes',
      'Assign users or groups from the "Users and groups" tab',
    ],
    provisioningNote: 'Note: Users are not automatically provisioned. Your organization\'s users must be invited to Tilaajakanava in advance and granted necessary permissions. Invitations are sent by a person with the organization admin role in Tilaajakanava.',
    mfaNote: 'We recommend enabling MFA (multi-factor authentication) according to your organization\'s policies.',
    
    errorTitle: 'Error',
  },
}

export default function OnboardingPage() {
  const [lang, setLang] = useState<Language>('fi')
  const [tenantId, setTenantId] = useState('')
  const [loading, setLoading] = useState(false)
  const [orgInfo, setOrgInfo] = useState<{
    exists: boolean
    organization_name?: string
    consent_verified?: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const t = translations[lang]

  const handleCheck = async () => {
    if (!tenantId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      setError(lang === 'fi' ? 'Virheellinen Tenant ID muoto' : 'Invalid Tenant ID format')
      return
    }

    setLoading(true)
    setError(null)
    setOrgInfo(null)

    try {
      const response = await fetch(
        `${API_URL}/onboarding/check-organization?tenant_id=${tenantId}`
      )
      
      if (response.status === 429) {
        setError(lang === 'fi' 
          ? 'Liian monta kyselyä. Yritä uudelleen tunnin kuluttua.' 
          : 'Too many requests. Please try again in an hour.')
        return
      }
      
      const data = await response.json()
      setOrgInfo(data)
    } catch (err) {
      setError(lang === 'fi' ? 'Yhteysvirhe. Yritä uudelleen.' : 'Connection error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleConsent = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/onboarding/callback`)
    const consentUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${CLIENT_ID}&redirect_uri=${redirectUri}&state=${tenantId}`
    window.location.href = consentUrl
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Language switcher */}
      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onClick={() => setLang('fi')}
          className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
            lang === 'fi' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Globe className="w-4 h-4" />
          Suomi
        </button>
        <button
          onClick={() => setLang('en')}
          className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm ${
            lang === 'en' ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Globe className="w-4 h-4" />
          English
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/images/logo-th.webp" 
            alt="Taksi Helsinki" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Info cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* What is this */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="font-semibold text-gray-900">{t.whatIsThis}</h2>
            </div>
            <p className="text-gray-600 text-sm">{t.whatIsThisDesc}</p>
          </div>

          {/* What happens */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="font-semibold text-gray-900">{t.whatHappens}</h2>
            </div>
            <p className="text-gray-600 text-sm mb-2">{t.whatHappensDesc}</p>
            <ul className="text-sm text-gray-600 space-y-1">
              {t.permissions.map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Process steps */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">{t.processTitle}</h2>
          <ol className="space-y-3">
            {t.processSteps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
          
          <div className="mt-6 pt-6 border-t space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tenant ID</label>
              <input
                type="text"
                value={tenantId}
                onChange={(e) => {
                  setTenantId(e.target.value)
                  setOrgInfo(null)
                  setError(null)
                }}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
              />
              <p className="mt-2 text-xs text-gray-500">{t.tenantIdHelp}</p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {orgInfo && !orgInfo.exists && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-5 h-5" />
                {t.orgNotFound}
              </div>
            )}

            {orgInfo && orgInfo.exists && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <CheckCircle2 className="w-5 h-5" />
                {t.orgFound}: <strong>{orgInfo.organization_name}</strong>
                {orgInfo.consent_verified && (
                  <span className="ml-2 px-2 py-0.5 bg-green-100 rounded text-xs">
                    {t.alreadyVerified}
                  </span>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleCheck}
                disabled={loading || !tenantId}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? t.checking : t.checkOrganization}
              </button>

              {orgInfo?.exists && (
                <button
                  onClick={handleConsent}
                  className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium"
                >
                  {t.grantConsent}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Admin tip - moved to end */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="font-semibold text-gray-900">{t.adminTip}</h2>
          </div>
          <p className="text-gray-700 text-sm mb-3">{t.adminTipDesc}</p>
          <ul className="text-sm text-gray-700 space-y-1 mb-3">
            {t.adminOptions.map((opt, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                <code className="bg-amber-100 px-1 rounded text-xs">{opt}</code>
              </li>
            ))}
          </ul>
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-3">
            <p className="text-sm text-amber-800 font-medium">{t.provisioningNote}</p>
          </div>
          <p className="text-sm text-amber-700">{t.mfaNote}</p>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Taksi Helsinki Oy
        </p>
      </div>
    </div>
  )
}
